<template>
  <div class="md-layout">
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <md-card>
        <md-card-content>
          <h3 class="md-headline h2">
            API Tokens
          </h3>

          <div class="table-header">
            <form
              class="search-form"
              @submit="onSearchSubmit"
            >
              <md-field
                md-clearable
                class="short-field"
                @md-clear="onClearSearch"
              >
                <label>Search tokens or comments</label>
                <md-input v-model="searchValue" />
              </md-field>
              <md-button
                type="submit"
                :disabled="loading"
              >
                Search
              </md-button>
            </form>

            <md-button
              class="md-primary"
              :disabled="loading"
              @click="createDialog.visible = true"
            >
              Create
            </md-button>
          </div>

          <md-progress-bar
            v-if="loading"
            md-mode="query"
          />
          <v-client-table
            v-else
            :data="apiTokens"
            :columns="table.columns"
            :options="table.options"
            class="table"
          >
            <template #api_token="props">
              <p class="api-token-col">
                {{
                  table.visibleTokens.includes(props.row.api_token)
                    ? props.row.api_token
                    : `...${props.row.api_token.slice(-10)}`
                }}
              </p>
            </template>

            <template #actions="props">
              <md-button
                class="md-icon-button"
                @click.stop="onVisibilityButtonClick(props.row.api_token)"
              >
                <md-icon>
                  {{
                    table.visibleTokens.includes(props.row.api_token)
                      ? 'visibility_off'
                      : 'visibility'
                  }}
                </md-icon>
              </md-button>

              <md-button
                class="md-icon-button"
                @click.stop="onCopyButtonClick(props.row.api_token)"
              >
                <md-icon>content_copy</md-icon>
              </md-button>
            </template>

            <template #created_at="props">
              {{ (new Date(props.row.created_at)).toUTCString() }}
            </template>

            <template #delete="props">
              <md-button
                class="md-icon-button"
                @click.stop="onDeleteClick(props.row.api_token, props.row.comment)"
              >
                <md-icon>delete</md-icon>
              </md-button>
            </template>
          </v-client-table>
        </md-card-content>
      </md-card>
    </div>

    <md-dialog
      :md-active.sync="createDialog.visible"
      :md-click-outside-to-close="false"
      @md-clicked-outside="onCreateDialogClose"
    >
      <md-dialog-title>Create API Key</md-dialog-title>

      <md-dialog-content>
        <md-field :class="$v.createDialog.comment.$error && 'md-invalid invalid-field'">
          <md-input v-model="createDialog.comment" />
          <label>Comment</label>
          <span class="md-error">Field is required</span>
        </md-field>

        <label :class="$v.createDialog.expiration_date.$error && 'invalid-field'">Expiration date:</label>
        <Datetime
          v-model="createDialog.expiration_date"
          type="datetime"
          zone="UTC"
          value-zone="UTC"
        />
        <span
          v-if="$v.createDialog.expiration_date.$error"
          class="invalid-field"
        >
          {{
            !$v.createDialog.expiration_date.mustBeInTheFuture
              ? 'Expiration date must be in the future'
              : 'Field is required'
          }}
        </span>

        <div class="create-token-wrapper">
          <div v-if="createDialog.createdToken">
            <b>Token crated:</b>
            <p class="create-token-token">
              {{ createDialog.createdToken }}
            </p>
            <md-button
              class="md-icon-button"
              @click.stop="onCopyDialogTokenClick"
            >
              <md-icon>content_copy</md-icon>
            </md-button>
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button
          :disabled="createDialog.loading"
          @click="onCreateDialogClose"
        >
          Close
        </md-button>
        <md-button
          v-if="!createDialog.createdToken"
          class="md-primary"
          :disabled="createDialog.loading"
          @click="onCreateDialogSubmit"
        >
          Save
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="deleteDialog.visible"
      md-title="Delete API token?"
      :md-content="`Token with the comment <b>${deleteDialog.comment}</b> will be deleted?`"
      md-confirm-text="Delete"
      md-cancel-text="Cancel"
      @md-cancel="onCancelDeleteClick"
      @md-confirm="onConfirmDeleteClick"
    />
  </div>
</template>

<script>
    import { required } from "vuelidate/lib/validators";
    import {Datetime} from "vue-datetime";
    import {Event} from "vue-tables-2-premium";

    export default {
        components: {
            Datetime
        },
        data() {
            return {
                apiTokens: [],
                loading: true,

                searchValue: "",

                table: {
                    columns: ["api_token", "actions", "comment", "created_at", "delete"],
                    options: {
                        filterable: true,
                        sortable: ["comment", "created_at"],
                        customSorting: {
                            created_at (ascending) {
                                return (a,b) => {
                                    const aMills = (new Date(a)).getTime();
                                    const bMills = (new Date(b)).getTime();

                                    if (ascending) {
                                        return aMills >= bMills ? 1 : -1;
                                    }

                                    return aMills <= bMills ? 1 : -1;
                                };
                            }
                        },
                        customFilters: [
                            {
                                name: "search",
                                callback(row, query) {
                                    if (query.length === 0) {
                                        return true;
                                    }
                                    return row.api_token.includes(query) || row.comment.includes(query);
                                }
                            }
                        ]
                    },
                    visibleTokens: []
                },

                createDialog: {
                    comment: "",
                    expiration_date: (new Date()).toISOString(),

                    visible: false,
                    loading: false,
                    createdToken: ""
                },

                deleteDialog: {
                    apiToken: "",
                    comment: "",

                    visible: false
                }
            };
        },
        mounted() {
            this.fetchTokens();
        },
        methods: {
            fetchTokens() {
                this.loading = true;
                this.$http.get("/v1/account/api-tokens").then(result => {
                    this.apiTokens = result.data.api_tokens;
                    this.loading = false;
                }, err => {
                    console.error(err);
                });
            },
            onVisibilityButtonClick (apiToken) {
                if (this.table.visibleTokens.includes(apiToken)) {
                    this.table.visibleTokens = this.table.visibleTokens.filter(t => t !== apiToken);
                } else {
                    this.table.visibleTokens = [...this.table.visibleTokens, apiToken];
                }
            },
            onCopyButtonClick (apiToken) {
                this.$copyText(apiToken).then(
                    () => {
                        this.$notify({
                            group: "foo",
                            title: "API token copied to clipboard",
                        });
                    },
                    (e) => {
                        this.$notify({
                            group: "foo",
                            title: "Couldn't copy API token((",
                        });
                        console.error(e);
                    }
                );
            },
            onCreateDialogClose () {
                if (!this.createDialog.loading) {
                    this.createDialog.visible = false;

                    // cleaning form up after creating new token
                    if (this.createDialog.createdToken) {
                        this.createDialog.createdToken = "";
                        this.createDialog.comment = "";
                        this.createDialog.expiration_date = (new Date()).toISOString();
                        this.fetchTokens();
                        this.$v.createDialog.$reset();
                    }
                }
            },
            onCreateDialogSubmit () {
                this.$v.createDialog.$touch();

                if (this.$v.createDialog.$error) {
                    return;
                }

                this.createDialog.loading = true;
                this.$http.post("/v1/account/api-token", {
                    comment: this.createDialog.comment,
                    expiration_date: this.createDialog.expiration_date
                }).then(
                    res => {
                        this.createDialog.createdToken = res.data.token;
                        this.createDialog.loading = false;
                    },
                    err => {
                        this.createDialog.loading = false;
                        this.$notify({
                            group: "foo",
                            title: `API Token creation failed: ${err}`,
                        });
                    }
                );
            },
            onCopyDialogTokenClick () {
                this.$copyText(this.createDialog.createdToken).then(
                    () => {
                        this.$notify({
                            group: "foo",
                            title: "API token copied to clipboard",
                        });
                    },
                    (e) => {
                        this.$notify({
                            group: "foo",
                            title: "Couldn't copy API token",
                        });
                        console.error(e);
                    }
                );
            },
            onDeleteClick (apiToken, comment) {
                this.deleteDialog.apiToken = apiToken;
                this.deleteDialog.comment = comment;

                this.deleteDialog.visible = true;
            },
            onConfirmDeleteClick () {
                this.$http.delete(`/v1/account/api-token/${this.deleteDialog.apiToken}`)
                    .then(
                        () => {
                            this.$notify({
                                group: "foo",
                                title: "API token deleted",
                            });
                            this.fetchTokens();
                            this.deleteDialog.visible = false;
                            this.deleteDialog.apiToken = "";
                            this.deleteDialog.comment = "";
                        },
                        (err) => {
                            this.$notify({
                                group: "foo",
                                title: "Couldn't delete API token",
                            });
                            console.error(err);
                        }
                    );
            },
            onCancelDeleteClick () {
                this.deleteDialog.visible = false;
                this.deleteDialog.apiToken = "";
                this.deleteDialog.comment = "";
            },
            onSearchSubmit (event) {
                event.preventDefault();
                Event.$emit("vue-tables.filter::search", this.searchValue);
            },
            onClearSearch () {
                Event.$emit("vue-tables.filter::search", "");
            }
        },
        validations: {
            createDialog: {
                comment: { required },
                expiration_date: {
                    required,
                    mustBeInTheFuture (value) {
                        const date = new Date(value);
                        const currentDate = new Date();

                        return date.getTime() > currentDate.getTime();
                    }
                }
            }
        }
    };
</script>
<style scoped lang="scss">
    .short-field {
        width: 600px;
    }

    .api-token-col {
        @media only screen and (min-width: 1500px) {
            width: 600px;
        }

        width: 400px;
        margin: auto;
    }

    .table-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 20px 30px 15px 30px;
        a {
            margin: 0;
        }

        .search-form {
            display: flex;
            align-items: center;

            button {
                margin: 0 0 0 6px;
                height: 40px;
            }
        }
    }

    .invalid-field {
        color: #ff1745;
        input, label {
            color: #ff1745 !important;
        }
    }

    .create-token-wrapper {
        height: 200px;
        margin: 40px auto 0;
    }
    .create-token-token {
        display: block;
        word-break: break-all
    }
</style>
